import { useState, useEffect, useCallback, useMemo } from 'react';
import { adminProjectsApi } from '../../projects/admin-api';
import { tenantsApi, type Tenant } from '../../api/tenants-api';
import { usersApi } from '../../users/api';
import type { Project } from '../../projects/types';
import type { User } from '../../auth/types';

/**
 * Interfaz para estadísticas del dashboard
 *
 * @remarks
 * Extendida para incluir métricas adicionales de usuarios y proyectos activos
 */
export interface DashboardStats {
  totalProjects: number;
  totalTenants: number;
  activeUsers: number; // DEPRECATED - usar activeTenants
  lastActivity: string | null;
  // NUEVAS MÉTRICAS
  activeProjects: number;
  activeTenants: number;
  totalUsers: number;
  activeUsersCount: number;
}

/**
 * Hook para gestión de datos del SuperAdmin dashboard
 *
 * @remarks
 * Centraliza toda la lógica de carga y actualización de datos para proyectos y tenants.
 * Calcula estadísticas en tiempo real para los KPI cards.
 *
 * @example
 * ```tsx
 * const { projects, tenants, stats, loading, refresh } = useSuperAdminData();
 * ```
 */
export function useSuperAdminData() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Carga inicial de datos (proyectos, tenants y usuarios)
   *
   * @remarks
   * Carga en paralelo para optimizar performance
   */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [projectsData, tenantsData, usersData] = await Promise.all([
        adminProjectsApi.list(),
        tenantsApi.getAll(),
        usersApi.list(), // Carga todos los usuarios (sin filtro de tenant)
      ]);

      setProjects(projectsData);
      setTenants(tenantsData);
      setUsers(usersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar datos');
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Calcula estadísticas para KPI cards
   *
   * @remarks
   * Usa useMemo para evitar recálculos innecesarios
   */
  const stats: DashboardStats = useMemo(() => {
    // Calcular última actividad
    const lastActivity =
      projects.length > 0
        ? [...projects].sort(
            (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          )[0].updatedAt
        : null;

    return {
      // Métricas existentes
      totalProjects: projects.length,
      totalTenants: tenants.length,
      activeUsers: tenants.filter((t) => t.status === 'active').length, // DEPRECATED
      lastActivity,
      // Nuevas métricas
      activeProjects: projects.length, // Project no tiene campo status — todos los proyectos se consideran activos
      activeTenants: tenants.filter((t) => t.status === 'active').length,
      totalUsers: users.length,
      activeUsersCount: users.filter((u) => u.isActive).length,
    };
  }, [projects, tenants, users]);

  /**
   * Refresca los datos del dashboard
   */
  const refresh = useCallback(async () => {
    await loadData();
  }, [loadData]);

  // Carga inicial
  useEffect(() => {
    void loadData();
  }, [loadData]);

  return {
    projects,
    tenants,
    users,
    stats,
    loading,
    error,
    refresh,
  };
}
